import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from '../constants/api';
import {
  AUTH_LOGOUT_REASON,
  AUTH_REASON_MESSAGE,
  AUTH_STORAGE,
  LogoutReason,
} from '../constants/auth';
import { USER_ACCESS_LEVEL, USER_ROLE, UserAccessLevel, UserRole } from '../constants/userManagement';

export interface AuthUser {
  id: number;
  username: string;
  role: UserRole;
  access_level: UserAccessLevel;
}

export interface LoginRequestPayload {
  username: string;
  password: string;
  resolve?: () => void;
  reject?: (error: unknown) => void;
}

export interface LogoutRequestPayload {
  reason?: LogoutReason;
  notifyServer?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  logoutReason: LogoutReason;
}

/** Reads the persisted auth user from local storage for the initial Redux state. */
const readStoredUser = (): AuthUser | null => {
  const raw = localStorage.getItem(AUTH_STORAGE.USER);
  return raw ? JSON.parse(raw) : null;
};

/** Reads and clears the one-time logout reason saved by API interceptors. */
const readStoredLogoutReason = (): LogoutReason => {
  const stored = sessionStorage.getItem(AUTH_STORAGE.LOGOUT_REASON) as LogoutReason;
  if (stored) sessionStorage.removeItem(AUTH_STORAGE.LOGOUT_REASON);
  return stored || null;
};

const initialState: AuthState = {
  user: readStoredUser(),
  token: localStorage.getItem(AUTH_STORAGE.TOKEN),
  loading: false,
  logoutReason: readStoredLogoutReason(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    hydrateAuthState: (state, action: PayloadAction<{ user: AuthUser | null; token: string | null; logoutReason?: LogoutReason }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.logoutReason = action.payload.logoutReason ?? null;
      state.loading = false;
    },
    loginRequest: (state, _action: PayloadAction<LoginRequestPayload>) => {
      state.loading = true;
    },
    loginSuccess: (state, action: PayloadAction<{ token: string; user: AuthUser }>) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.logoutReason = null;
      state.loading = false;
    },
    loginFailure: (state) => {
      state.loading = false;
    },
    logoutRequest: (_state, _action: PayloadAction<LogoutRequestPayload | undefined>) => {},
    logoutLocal: (state, action: PayloadAction<LogoutReason | undefined>) => {
      state.token = null;
      state.user = null;
      state.logoutReason = action.payload ?? AUTH_LOGOUT_REASON.MANUAL;
      state.loading = false;
    },
    refreshServerActivityRequest: (_state) => {},
  },
});

export const authActions = authSlice.actions;

export const selectAuthUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectLogoutReason = (state: { auth: AuthState }) => state.auth.logoutReason;
export const selectCanWrite = (state: { auth: AuthState }) =>
  state.auth.user?.role === USER_ROLE.ADMIN || state.auth.user?.access_level === USER_ACCESS_LEVEL.WRITE;

/** Returns the display message for an auth logout reason. */
export const getReasonMessage = (reason: LogoutReason): string => {
  if (!reason || reason === AUTH_LOGOUT_REASON.MANUAL) return '';
  return AUTH_REASON_MESSAGE[reason] || AUTH_REASON_MESSAGE[AUTH_LOGOUT_REASON.INVALID_TOKEN];
};

export const authApi = {
  loginUrl: API_ENDPOINTS.AUTH.LOGIN,
  logoutUrl: API_ENDPOINTS.AUTH.LOGOUT,
  meUrl: API_ENDPOINTS.AUTH.ME,
};

export default authSlice.reducer;
